import React from 'react';
import { motion } from 'framer-motion';
import { FiPrinter, FiBox, FiStar } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useScrollAnim, V } from '../hooks/useScrollAnimation';

const EXPANSIONS = [
  {
    Icon: FiPrinter,
    tag: "Expansion Area",
    title: "Corporate Printing Solutions",
    desc: "Custom apparel printing, promotional materials, and branding support designed for businesses, events, institutions, and organizations.",
    features: ["Corporate Apparel", "Promotional Printing", "Event Merchandise", "Bulk Production"],
    color: "#E91E8C"
  },
  {
    Icon: FiBox,
    tag: "Expansion Area",
    title: "Industrial Product Solutions",
    desc: "Future expansion into industrial and commercial product categories to support procurement, sourcing, and business operations.",
    features: ["Industrial Supplies", "Commercial Products", "Business Procurement", "Supply Solutions"],
    color: "#6B21A8"
  },
  {
    Icon: FiStar,
    tag: "Expansion Area",
    title: "Branding & Identity Solutions",
    desc: "Helping businesses strengthen their market presence through branding, merchandise, promotional products, and visual identity solutions.",
    features: ["Brand Development", "Merchandise Solutions", "Promotional Products", "Corporate Identity"],
    color: "#FF6B2B"
  }
];

export default function Future() {
  const { ref, controls } = useScrollAnim(0.1);

  return (
    <section id="future" style={{
      padding: 'var(--spy) 0',
      background: 'linear-gradient(150deg,#fff 0%,#FFF5F9 40%,#FFFBF5 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div className="dot-grid" style={{ position: 'absolute', inset: 0, opacity: 0.5, pointerEvents: 'none' }} />

      <div className="container" ref={ref}>
        {/* Header */}
        <motion.div
          initial="hidden"
          animate={controls}
          variants={V.stagger}
          style={{ textAlign: 'center', marginBottom: '4rem' }}
        >
          <motion.span
            variants={V.child}
            style={{
              display: 'block',
              fontFamily: 'var(--font-body)',
              fontSize: '0.78rem',
              fontWeight: 700,
              background: 'linear-gradient(135deg,#E91E8C,#FF6B2B)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textTransform: 'uppercase',
              letterSpacing: '0.15em',
              marginBottom: '0.8rem'
            }}
          >
            OUR FUTURE VISION
          </motion.span>

          <motion.h2
            variants={V.child}
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: 'clamp(2.3rem, 5.2vw, 3.5rem)',
              color: 'var(--text)',
              lineHeight: 1.1,
              marginBottom: '1.1rem'
            }}
          >
            Building The Future{' '}
            <span style={{
              background: 'linear-gradient(135deg,#E91E8C,#FF6B2B)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Of SPE
            </span>
          </motion.h2>

          <motion.p
            variants={V.child}
            style={{
              fontFamily: 'var(--font-body)',
              color: 'var(--text-muted)',
              fontSize: '1.05rem',
              maxWidth: 680,
              margin: '0 auto',
              lineHeight: 1.75
            }}
          >
            As part of our long-term vision, SPE is continuously expanding its capabilities to deliver broader 
            business solutions and create greater value for clients across multiple industries.
          </motion.p>
        </motion.div>

        {/* Growth Roadmap Label */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ display: 'flex', justifyContent: 'center', marginBottom: '2.5rem' }}
        >
          <div style={{
            background: 'rgba(107,33,168,0.08)',
            border: '1.5px solid rgba(107,33,168,0.25)',
            borderRadius: 99,
            padding: '0.55rem 1.6rem',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.6rem'
          }}>
            <span style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: 'linear-gradient(135deg,#E91E8C,#6B21A8)'
            }} />
            <span style={{
              fontFamily: 'var(--font-body)',
              fontWeight: 700,
              fontSize: '0.8rem',
              letterSpacing: '0.1em',
              color: '#6B21A8',
              textTransform: 'uppercase'
            }}>
              GROWTH ROADMAP
            </span>
          </div>
        </motion.div>

        {/* Expansion Cards */}
        <motion.div
          initial="hidden"
          animate={controls}
          variants={V.stagger2}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem',
            marginBottom: '5rem'
          }}
        >
          {EXPANSIONS.map((item, index) => (
            <motion.div
              key={index}
              variants={V.child}
              whileHover={{ y: -12, boxShadow: '0 25px 60px rgba(233,30,140,0.18)' }}
              style={{
                background: '#fff',
                borderRadius: 26,
                padding: '2.5rem 2rem',
                border: '1.5px solid rgba(233,30,140,0.1)',
                boxShadow: 'var(--shadow-card)',
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}
            >
              <div style={{
                width: 68,
                height: 68,
                borderRadius: 18,
                background: `linear-gradient(135deg, ${item.color}15, transparent)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.6rem',
                color: item.color,
                border: `1px solid ${item.color}25`
              }}>
                <item.Icon size={32} />
              </div>

              <span style={{
                fontSize: '0.73rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                color: '#6B21A8',
                textTransform: 'uppercase',
                marginBottom: '0.8rem'
              }}>
                {item.tag}
              </span>

              <h3 style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 700,
                fontSize: '1.45rem',
                lineHeight: 1.25,
                marginBottom: '1rem'
              }}>
                {item.title}
              </h3>

              <p style={{
                fontFamily: 'var(--font-body)',
                color: 'var(--text-muted)',
                lineHeight: 1.7,
                marginBottom: '1.8rem',
                flex: 1
              }}>
                {item.desc}
              </p>

              {/* Features */}
              <div style={{ marginTop: 'auto' }}>
                {item.features.map((feature, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '0.88rem',
                    color: 'var(--text-muted)',
                    marginBottom: '0.45rem'
                  }}>
                    <span style={{ color: '#22C55E' }}>✓</span>
                    {feature}
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Vision CTA Banner */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          style={{
            borderRadius: 28,
            padding: 'clamp(3rem, 6vw, 4.5rem)',
            textAlign: 'center',
            background: 'linear-gradient(135deg, #2D0A54, #4A148C, #6B21A8)',
            boxShadow: '0 25px 70px rgba(45,10,84,0.4)',
            position: 'relative',
            overflow: 'hidden',
            color: '#fff'
          }}
        >
          <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: 320, height: 320, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          <div style={{ position: 'absolute', bottom: '-60px', left: '-60px', width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />

          <div style={{ position: 'relative' }}>
            <h3 style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: 'clamp(1.8rem, 4.5vw, 2.6rem)',
              marginBottom: '1rem'
            }}>
              Driven By Innovation. Focused On Growth.
            </h3>

            <p style={{
              fontFamily: 'var(--font-body)',
              color: 'rgba(255,255,255,0.85)',
              fontSize: '1.05rem',
              maxWidth: 680,
              margin: '0 auto 2.5rem',
              lineHeight: 1.75
            }}>
              SPE is committed to expanding its capabilities and delivering new business solutions that help 
              clients operate more efficiently, strengthen their brands, and support long-term growth.
            </p>

            <motion.a
              href="https://wa.me/919821991932?text=Hi SPE! I would like to discuss your future expansion plans and potential partnership opportunities."
              target="_blank"
              rel="noreferrer"
              whileHover={{ scale: 1.06 }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 12,
                padding: '1.15rem 2.6rem',
                background: '#fff',
                color: '#4A148C',
                fontWeight: 700,
                fontSize: '1.02rem',
                borderRadius: 99,
                textDecoration: 'none',
                boxShadow: '0 12px 35px rgba(0,0,0,0.25)'
              }}
            >
              <FaWhatsapp size={20} color="#25D366" />
              Connect With SPE
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}